import { useQuery } from "@tanstack/react-query";

import { api } from "../../api/client";
import type { LicenceStatus } from "../../api/types";
import { config } from "../../config";
import { LICENCE_QUERY } from "../../licence/licence";

/** Terrain libre ouvert sur ce serveur (réglage `GENEREX_ZONES_ENABLED`, registre K25 ; ouvert en production depuis le
 *  03/10/2026), lu dans l'état de licence (`zones_enabled`) : fermé tant que l'état n'est pas connu. Fermé, aucun
 *  nouveau terrain n'est proposé (Données initiales, vue Manœuvre) ; les terrains existants restent affichés et
 *  l'espace de stockage aussi. */
export function zonesOpen(status: Pick<LicenceStatus, "zones_enabled"> | undefined | null): boolean {
  return status?.zones_enabled === true;
}

/** `zonesOpen` de l'état de licence en cache (requête partagée avec le portail de licence). */
export function useZonesOpen(): boolean {
  const { data } = useQuery({ queryKey: LICENCE_QUERY, queryFn: api.licence, staleTime: 5 * 60_000 });
  return zonesOpen(data);
}

/** Règle d'offre du terrain libre, unique pour les Données initiales et la vue Manœuvre (registre, 03/10/2026) : un
 *  nouveau terrain n'est proposé que si le serveur l'a ouvert, que la carte mondiale (Mapbox) est là et hors CENTAC
 *  (la rotation se joue au camp de Mailly). */
export function freeTerrainOffered(o: { open: boolean; mapbox: boolean; centac: boolean }): boolean {
  return o.open && o.mapbox && !o.centac;
}

/** Pourquoi le terrain libre n'est pas proposé (null s'il l'est), dans l'ordre : CENTAC, serveur fermé, carte
 *  mondiale absente. */
export type FreeTerrainUnavailable = "closed" | "no-world-map" | "centac";

export function freeTerrainReason(o: { open: boolean; mapbox: boolean; centac: boolean }): FreeTerrainUnavailable | null {
  if (o.centac) return "centac";
  if (!o.open) return "closed";
  if (!o.mapbox) return "no-world-map";
  return null;
}

/** `freeTerrainOffered` d'après l'état de licence en cache et le moteur de carte ; `known` : état de licence reçu
 *  (avant, rien n'est proposé, et rien n'est expliqué non plus). */
export function useFreeTerrainOffer(centac: boolean): { offered: boolean; known: boolean; reason: FreeTerrainUnavailable | null } {
  const { data } = useQuery({ queryKey: LICENCE_QUERY, queryFn: api.licence, staleTime: 5 * 60_000 });
  const rule = { open: zonesOpen(data), mapbox: config.mapEngine === "mapbox", centac };
  return { offered: freeTerrainOffered(rule), known: data !== undefined, reason: freeTerrainReason(rule) };
}
